import dayjs from "dayjs";
import { ActionButton, DefaultButton, PrimaryButton } from '@fluentui/react/lib/Button';
import '../App.sass';
import { Text } from '@fluentui/react/lib/Text';
import Section from './Section';
import { useTranslation } from "react-i18next";
import { pdf, PDFDownloadLink } from '@react-pdf/renderer';
import { useEffect, useState } from 'react';
import { useAdministrationAPI } from '../AdministrationAPI';
import { authenticationAtom } from '../State';
import { useAtom } from 'jotai';
import Dialog, { DialogType, DialogFooter } from '@fluentui/react/lib/Dialog';
import { userAPI } from '../UserAPI';
import { Dropdown, IDropdownOption } from '@fluentui/react/lib/Dropdown';
import { Address, Customer, from, Invoice, InvoiceStatus, InvoiceType, is, LegalPerson, Plan, Project, RelationshipDirection } from "@the-real-insight/tri-model";
import DataTable from 'react-data-table-component';
import { dataTableStyles } from '../styles';
import { Checkbox } from '@fluentui/react/lib/Checkbox';
import { TooltipHost } from '@fluentui/react/lib/Tooltip';
import LoadingIndicator from "./LoadingIndicator";
import { useModalContext } from "./ModalProvider";
import { PDFInvoice, generateEInvoice, downloadInvoice, downloadFile } from '../billing/invoice';
import React from 'react';
import { Document, Page } from '@react-pdf/renderer';
import { pdfStyles } from '../pdf/PDF';

interface Properties {
    project: Project,
    projectChanged: (project: Project) => void,
}

export default function InvoicePanel(properties: Properties) {
    const { t } = useTranslation();
    const [authentication] = useAtom(authenticationAtom) as any;
    const { pushError } = useModalContext() as any;
    const { getInvoiceForecast, getInvoices } = useAdministrationAPI();
    const [customer, setCustomer] = useState() as any;
    const [order, setOrder] = useState() as any;
    const [currentBillingPeriodFrom, setCurrentBillingPeriodFrom] = useState(dayjs().startOf('M').toDate()) as any;
    const [currentBillingPeriodTo, setCurrentBillingPeriodTo] = useState(dayjs().endOf('M').toDate()) as any;
    const [currentInvoices, setCurrentInvoices] = useState() as any;
    const [currentInvoicePDF, setCurrentInvoicePDF] = useState() as any;
    const currencyFormat = new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' });
    const [planDialogOpen, setPlanDialogOpen] = useState(false);
    const [orders, setOrders] = useState([]) as any;
    const [invoices, setInvoices] = useState([]) as any;
    const [plans, setPlans] = useState() as any;
    const [portalAndAppUsagePlan, setPortalAndAppUsagePlan] = useState() as any;
    const [privateDataIngestionPlan, setPrivateDataIngestionPlan] = useState() as any;

    const portalAndAppUsageItems = (): IDropdownOption[] => plans && plans.portalAndAppUsage ? plans.portalAndAppUsage.map((plan: any) => {
        return {
            key: plan.id,
            text: plan.name
        };
    }) : [];

    const privateDataIngestionItems = (): IDropdownOption[] => plans && plans.privateDataIngestion ? [{ key: null, text: t('global.none') }, ...plans.privateDataIngestion.map((plan: any) => {
        return {
            key: plan.id,
            text: plan.name
        };
    })] : [];

    const planSelectionComplete = (): boolean => {
        return portalAndAppUsagePlan !== undefined;
    };

    const findPlan = (plans: any, id: string) => plans.find((plan: any) => plan.id === id);

    useEffect(() => {
        if (!properties.project) {
            return;
        }

        const call = async () => {
            const newCustomer = await userAPI.getCustomer(authentication.customer);

            newCustomer.address = newCustomer.address || {};

            setCustomer(newCustomer);

            // Current service orders

            const newOrder = await userAPI.fullOrderForProject(authentication.customer, properties.project._id as any);

            console.log('Order >>>', newOrder);

            if (!newOrder) {
                throw 'No order for project';
            }

            const periodFromDate = newOrder.startOfBillingPeriod();
            const periodToDate = newOrder.endOfBillingPeriod();

            console.log('Period from >>>', periodFromDate);
            console.log('Period to >>>', periodToDate);

            setOrder(newOrder);

            console.log('Project >>>', properties.project);
            console.log('Service Orders >>>', newOrder);

            setCurrentBillingPeriodFrom(periodFromDate);
            setCurrentBillingPeriodTo(periodToDate);

            const invoiceForecast = properties.project ? await userAPI.getInvoiceForecastForProject(authentication.customer, properties.project._id as any,
                periodFromDate, periodToDate) : await getInvoiceForecast(authentication.customer,
                    periodFromDate, periodToDate);

            console.log('Invoice forecast >>>', invoiceForecast);

            setCurrentInvoices(invoiceForecast);

            // if (customer && invoiceForecast) {
            //     setCurrentInvoicePDF(<PDFInvoice invoice={invoiceForecast} customer={customer}></PDFInvoice>);
            // }

            const newInvoices = (await getInvoices(authentication.customer, new Date('2024-05-01T00:00:00'), new Date()))
                .sort((a: Invoice, b: Invoice) => b.periodFromDate.getTime() - a.periodFromDate.getTime());

            console.log('Invoices >>>', newInvoices);

            setInvoices(newInvoices);
        };

        try {
            call();
        } catch (error: any) {
            pushError(error);
        }
    }, [properties.project]);

    const togglePlanDialogOpen = () => {
        setPlanDialogOpen(!planDialogOpen);
    }

    const columns = [{
        name: t('billingPanel.paymentStatusColumn'),
        width: '100px',
        cell: (invoice: Invoice) => (
            <div className="width50 paddingS displayFlex">
                <div className={(invoice.status === InvoiceStatus.paid ? 'colorGreen' : 'colorRed') + ' fontWeightBold textM'}>{invoice.status === InvoiceStatus.paid ? 'Bezahlt' : 'Offen'}</div>
            </div>
        ),
    }, {
        name: t('billingPanel.invoiceDateColumn'),
        width: '150px',
        right: true,
        cell: (invoice: Invoice) => (
            <Text variant="medium">{dayjs(invoice.creationDate).format(t('global.dateShort'))}</Text>
        ),
    }, {
        name: t('billingPanel.invoicePeriodColumn'),
        width: '200px',
        cell: (invoice: Invoice) => (
            <Text variant="medium">{dayjs(invoice.periodFromDate).format(t('global.dateShort'))} - {dayjs(new Date(invoice.periodToDate)).format(t('global.dateShort'))}</Text>
        ),
    }, {
        name: t('billingPanel.amountColumn'),
        width: '120px',
        right: true,
        cell: (invoice: Invoice) => (
            <Text variant="medium">{currencyFormat.format(invoice.subtotal())}</Text>
        ),
    }, {
        name: t('billingPanel.downloadColumn'),
        width: '100px',
        cell: (invoice: any) => (
            <div className='displayFlex'>
                <TooltipHost content="Download invoice forecast PDF">
                    <ActionButton iconProps={{ iconName: 'PDF' }} onClick={async () => {
                        const doc = (
                            <Document>
                                <Page size="A4" style={pdfStyles.page}>
                                    <PDFInvoice invoice={invoice} customer={customer} />
                                </Page>
                            </Document>
                        );
                        const blob = await pdf(doc).toBlob();
                        downloadFile(blob, `Rechnung ${invoice.id}`);
                    }}>
                    </ActionButton>
                </TooltipHost>
                <TooltipHost content="Download invoice forecast as E-Rechnung">
                    <ActionButton iconProps={{ iconName: 'FileCode' }} onClick={() => downloadInvoice(invoice)}>
                    </ActionButton>
                </TooltipHost>
            </div>
        ),
    },
    ];

    return <><div>
        <div className='displayFlex gapL'>
            <div className="displayFlex flexDirectionColumn width800 gapL">
                <Section title={`Aktueller Abrechnungszeitraum ${dayjs(currentBillingPeriodFrom).format(t('global.dateShort'))} - ${dayjs(currentBillingPeriodTo).format(t('global.dateShort'))}`}>
                    {currentInvoices
                        ?
                        <>
                            {currentInvoices.map((invoice: Invoice) => (
                                <div key={invoice.id}>
                                    <div key={invoice.id} className='displayFlex justifyContentEnd'>
                                        <TooltipHost content="Download invoice forecast PDF">
                                            <ActionButton iconProps={{ iconName: 'PDF' }} onClick={async () => {
                                                const doc = (
                                                    <Document>
                                                        <Page size="A4" style={pdfStyles.page}>
                                                            <PDFInvoice invoice={invoice} customer={customer} />
                                                        </Page>
                                                    </Document>
                                                );
                                                const blob = await pdf(doc).toBlob();
                                                downloadFile(blob, `Rechnung ${invoice.id}`);
                                            }}>
                                            </ActionButton>
                                        </TooltipHost>
                                        <TooltipHost content="Download invoice forecast as E-Rechnung">
                                            <ActionButton iconProps={{ iconName: 'FileCode' }} onClick={() => downloadInvoice(invoice)}>
                                            </ActionButton>
                                        </TooltipHost>
                                    </div>
                                    <div className='displayFlex alignItemsCenter gapS'>
                                        <Text className="fontWeightBold" variant="medium">Bisheriger Betrag</Text>
                                        <Text variant="xxLarge">{currencyFormat.format(invoice.grandTotal(0.19))}</Text>
                                    </div>
                                    <div className='marginTopL displayFlex'>
                                        {invoices && invoices.length > 0
                                            ?
                                            <div className='flexGrow1'>
                                                <div>
                                                    <Text className="fontWeightBold" variant="medium">Letzte Rechnung bis</Text>
                                                </div>
                                                <div>
                                                    <Text variant="medium">{dayjs(invoice.periodToDate).format(t('global.dateShort'))}</Text>
                                                </div>
                                            </div>
                                            :
                                            <></>
                                        }
                                    </div>
                                    <div className='marginTopL displayFlex justifyContentEnd'>
                                        {currentInvoicePDF ?
                                            <PDFDownloadLink document={currentInvoicePDF} fileName="Abrechnung Juli 2024.pdf">
                                                {/* <>{({ blob, url, loading, error }: any) =>
                                                    loading
                                                        ?
                                                        'Loading document...'
                                                        :
                                                        <PrimaryButton iconProps={{ iconName: 'Download' }}>
                                                            Rechnungsvorschau
                                                        </PrimaryButton>
                                                }</> */}
                                            </PDFDownloadLink>
                                            :
                                            <></>
                                        }
                                    </div>
                                </div>
                            ))}
                        </>
                        :
                        <></>}
                </Section>
                <Section title={t('billingPanel.invoices')}>
                    <div className='displayFlex justifyContentEnd'>
                        <ActionButton onClick={async () => {
                            console.log('Creating missing imnvoices');
                            // @ts-ignore
                            await userAPI.createMissingInvoicesForProject(authentication.customer, properties.project._id);
                        }}>
                            {t('billingPanel.createInvoice')}
                        </ActionButton>
                    </div>
                    <DataTable
                        columns={columns}
                        data={invoices}
                        customStyles={dataTableStyles}
                        pagination
                        noDataComponent={<LoadingIndicator loading={false} text={t('settingsPanel.projectsPanel.noObjectsDefined')}></LoadingIndicator>}
                    />
                </Section>
            </div>
            <div className='displayFlex flexDirectionColumn gapL width30vw'>
                <Section title="Abrechnungsmodalitäten">
                    <Dropdown
                        label="Rechnungsart"
                        options={[
                            { key: 'direct', text: 'Direkt an Endkunden' },
                            { key: 'indirect', text: 'Indirekt über Partner' }]}
                        selectedKey={properties.project.invoiceType}
                        onChange={(_: any, option: any) => { properties.projectChanged(new Project({ ...properties.project, invoiceType: option.key })) }}
                        styles={{
                            dropdown: { width: 300 },
                        }}
                    />
                    <div className="marginTopM">
                        <Checkbox label="E-Mail an Kundenkontakt" onChange={() => { }} />
                        <Checkbox className="marginTopS" label="E-Mail an Nutzende des Partners in Rolle Abrechnung" onChange={() => { }} />
                    </div>
                </Section>
                <Section title="Aktuelle Lizenzen">
                    {order && properties.project
                        ?
                        <>
                            <ul>
                                {order.orderItems.map((orderItem: any) =>
                                    <li key={orderItem.service.name + orderItem.plan.description}>
                                        <Text><span className='fontWeightBold'>{orderItem.service.name}:</span> {orderItem.plan.description}</Text>
                                    </li>
                                )}
                            </ul>
                        </>
                        :
                        <>
                            <ul>
                                <li>
                                    <Text><div className='fontWeightBold'>Installation Digitaler Feuerlöscher</div> 120,00€</Text>
                                </li>
                                <li>
                                    <Text><div className='fontWeightBold'>Wartung Digitaler Feuerlöscher</div> 24,00€/Quartal</Text>
                                </li>
                            </ul>
                        </>
                    }
                    <div className='marginTopL displayFlex justifyContentEnd'>
                        <PrimaryButton text="Ändern" onClick={() => { togglePlanDialogOpen() }} />
                    </div>
                </Section>
            </div>
        </div>
    </div>
        <Dialog hidden={!planDialogOpen}
            onDismiss={togglePlanDialogOpen}
            maxWidth={600}
            minWidth={600}
            dialogContentProps={{
                type: DialogType.normal,
                title: t('settingsPanel.billingPanel.licenses'),
            }}
            modalProps={{
                isBlocking: true,
            }}>
            <Dropdown
                placeholder="Select an option"
                label="Nutzung von Portal und App"
                options={portalAndAppUsageItems()}
                styles={{
                    dropdown: { width: 400 },
                    dropdownOptionText: { overflow: 'visible', whiteSpace: 'normal' },
                    dropdownItem: { height: 'auto' },
                }}
                selectedKey={portalAndAppUsagePlan ? portalAndAppUsagePlan.id : undefined}
                onChange={(event: any, item: any) => {
                    if (plans.portalAndAppUsage) {
                        setPortalAndAppUsagePlan(plans.portalAndAppUsage.find((plan: any) => plan.id === item.key));
                    }
                }}
            />
            <div className="marginTopS height50">
                <Text variant='small'>{portalAndAppUsagePlan ? portalAndAppUsagePlan.description : ''}</Text>
            </div>
            <Dropdown
                placeholder="Select an option"
                label="Management privater Daten"
                options={privateDataIngestionItems()}
                styles={{
                    dropdown: { width: 400 },
                    dropdownOptionText: { overflow: 'visible', whiteSpace: 'normal' },
                    dropdownItem: { height: 'auto' },
                }}
                selectedKey={privateDataIngestionPlan ? privateDataIngestionPlan.id : undefined}
                onChange={(event: any, item: any) => {
                    if (plans.privateDataIngestion) {
                        setPrivateDataIngestionPlan(plans.privateDataIngestion.find((plan: any) => plan.id === item.key));
                    }
                }}
            />
            <div className="marginTopS  height50">
                <Text variant='small'>{privateDataIngestionPlan ? privateDataIngestionPlan.description : ''}</Text>
            </div>
            <DialogFooter className="marginTopXXL">
                <PrimaryButton disabled={!planSelectionComplete()} text={t('global.change')} onClick={async () => {
                }} />
                <DefaultButton onClick={togglePlanDialogOpen} text={t('global.cancel')} />
            </DialogFooter>
        </Dialog></>;
}